// plannedEdit.mjs — 规划工时行「改/删」的纯判断（第八十四批立，线 B 补齐 R-08）。
//
// ── 病灶 ────────────────────────────────────────────────────────────────────
// work-hours-rules 的 R-08 写着「规划记录在 d 抵达前可由本人改/删」，store 层也早已
// 实现（替代式追加：原行置 released、追加新行、编辑重跑 R-01/R-11），**但没有任何
// 到达路径**——工时页只有"冲正"一个入口，而冲正（R-36）**明确不受理 planned 行**。
// 于是成员录错一笔计划工时，在界面上是一条死路：改不了、删不掉，也没有任何提示
// 告诉他该往哪走。
//
// ── 这里为什么必须是纯函数 ──────────────────────────────────────────────────
// "哪些行该出现编辑/删除按钮"这件事，错的方向有两个，且两个都很贵：
//   · 漏给 —— 规划行没有入口，等于本模块要补的洞原样还在；
//   · 多给 —— **已投入的行上出现一个删不掉的删除按钮**。R-36 的语义是那种行
//     删不掉（OQ-C21：管理员也不行），点下去必然被服务端拒。一个必然失败的按钮
//     比没有按钮更坏：它把"这件事做不到"伪装成"你操作失败了"。
// 判断因此从 JSX 里拆出来配单测，而不是写成一行内联三元式没人看得见。
//
// 口径与服务端对齐：`GET /api/me/work-log` 的读路径每次先跑 tick()（R-09 补执行），
// 所以报文里还显示 `planned` 的行，就是**到期前**的行。客户端不自己拿本机时钟推
// 日界——本机时区与账号时区不同，推出来的"到期没到期"会是错的。

// 一行能不能改/删（R-08）。三条同时成立：
//   ① 状态是 planned —— invested / released / migrated / friction 一律不行；
//   ② direction === 1 —— 冲正行（direction=-1）不是规划行，它自己也不可改删（R-38）；
//   ③ 调用方有 log_work_hours 权限位（与录入、冲正同一位）。
// 归属（"是不是本人的"）不在这里判：`/api/me/work-log` 只回本人的行，
// 客户端再判一遍只会引入第二份口径。
export function mayEditPlanned(row, mayLog) {
  if (!row || !mayLog) return false;
  return row.status === 'planned' && Number(row.direction) === 1;
}

// 一行能不能冲正（R-36）。此前这一段以内联三元式写在 WorkHoursPage 里
// （`r.direction === 1 && r.status === 'invested'`）；搬到这里是为了让它与上面那条
// **并排**——两个入口互斥这件事，只有摆在一起时才看得出来有没有被写反。
export function mayReverse(row, mayLog) {
  if (!row || !mayLog) return false;
  return row.status === 'invested' && Number(row.direction) === 1;
}

// 两个入口**永不同时出现**在同一行上。这是 R-08 与 R-36 的分工在界面上的样子：
// 规划态归改/删，投入态归冲正，没有中间地带。判据化是因为"互斥"这种性质在
// 两处各写一个条件时最容易被悄悄破坏。
export function rowActionsOf(row, mayLog) {
  return { edit: mayEditPlanned(row, mayLog), reverse: mayReverse(row, mayLog) };
}

// 编辑表单 → PATCH 请求体。返回 { patch, error }：
//   · error 非空 ⇒ 不发请求，把 error（一个 i18n 键）显示出来；
//   · patch 只带**真的变了**的字段——两个都没变时是空改动，服务端会 400，
//     而在这里就能说清"你什么都没改"，不必用一次往返换一句话。
// 窗口（R-01）与封顶（R-11）**不在这里复制一份**：那是服务端的规则，客户端复制的
// 副本只会漂移。这里只挡两件客户端确实知道的事：0.5 小时步进（R-05，与录入表单
// 同一个校验器）与"什么都没改"。
export function buildPlannedPatch(row, { day, minutes }) {
  if (!row) return { patch: null, error: 'work.edit.error.noRow' };
  const nextDay = Number(day);
  if (!Number.isInteger(nextDay) || nextDay <= 0) {
    return { patch: null, error: 'work.edit.error.day' };
  }
  const nextMinutes = Number(minutes);
  if (!Number.isInteger(nextMinutes) || nextMinutes <= 0 || nextMinutes % 30 !== 0) {
    return { patch: null, error: 'work.error.halfHour' };
  }
  const patch = {};
  if (nextDay !== Number(row.workDay)) patch.workDay = nextDay;
  if (nextMinutes !== Number(row.minutes)) patch.minutes = nextMinutes;
  if (Object.keys(patch).length === 0) return { patch: null, error: 'work.edit.error.noChange' };
  return { patch, error: '' };
}
